/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useMemo, useEffect } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import FeatherIcon from 'feather-icons-react';
import { Form, Spinner, Modal } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { Dispatch } from 'redux';
import { useQuery } from 'react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faBarcode, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom';

import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import orderItemService from 'service/orderItem';
import Button from 'components/base/Button';
import { pageToOffset } from 'utils/pageToOffset';
import { showAlert } from 'store/alert/alert.thunk';
import AdvanceTableCPagination from 'components/base/AdvanceTableCPagination';
import orderService from 'service/order';
import orderOutService from 'service/orderOut';
import legalEntitySettingsService from 'service/legalEntitySettings';
import AddIsForsedQuantity from './AddIsForsedQuantity';
import { useWindowWidth } from 'utils/useWindowSize';

export type SearchDataType = {
  product_name: string;
  variation: string;
  barcode: string;
  units: string;
  gupid: number;
  amount: number;
  total_quantity: number;
  price_whosale: any;
  quantity_asked: any;
  measure_unit_kind_pid: number;
  package_quantity: any;
  is_selected: boolean;
};

interface NewAppSearchTableProps {
  chartererData?: any;
  setOpenSearch?: any;
  isHandleSave?: any;
  setIshandleSave?: any;
  selectedData?: any;
  setSelectedData?: any;
  setOrderItemData?: any;
}

const NewAppSearchTable = ({
  chartererData,
  setOpenSearch,
  isHandleSave,
  setIshandleSave,
  selectedData,
  setSelectedData,
  setOrderItemData
}: NewAppSearchTableProps) => {
  const { t, i18n } = useTranslation();
  const { state, pathname } = useLocation();
  const { sender_pid, currency_pid } = state;
  const dispatch: Dispatch<any> = useDispatch();
  const newOrderPid = Number(pathname.split('/').pop() || 0);
  const windowSize: any = useWindowWidth();

  const [searchValue, setSearchValue] = useState('');
  const [debouncedSearchValue, setDebouncedSearchValue] = useState(searchValue);
  const [getBarcode, setGetBarcode] = useState('');
  const [pageCount, setPageCount] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingButton, setLoadingButton] = useState(false);
  const [openAddProduct, setOpenAddProduct] = useState(false);
  const [isForceQuantity, setIsForceQuantity] = useState(false);
  const [addedProduct, setAddedProduct] = useState<any>();
  const [isExisted, setIsExisted] = useState(false);

  const fetchDataLegalSettings = useMemo(() => {
    return () => {
      legalEntitySettingsService
        .getList({ offset: 0, limit: 100 })
        .then((res: any) => {
          setIsForceQuantity(
            res?.legal_entity_settings[0]?.is_force_quantity ?? false
          );
        });
    };
  }, []);

  useEffect(() => {
    fetchDataLegalSettings();
  }, []);
  // Debounce effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
      setCurrentPage(1);
    }, 400);

    return () => clearTimeout(handler);
  }, [searchValue]);

  const { data, isLoading, isFetching } = useQuery(
    [
      'GET_ORDER_ITEMS',
      debouncedSearchValue,
      sender_pid,
      currency_pid,
      currentPage,
      getBarcode,
      isExisted
    ],
    () => {
      if (sender_pid !== 0) {
        const res = orderItemService
          .getByLegalEntityId(sender_pid, {
            limit: 10,
            offset: pageToOffset(currentPage, 10),
            name: debouncedSearchValue,
            barcode: getBarcode,
            currency_pid: currency_pid,
            is_exist: isExisted ? false : true,
            order_pid: newOrderPid
          })
          .then((res: any) => {
            return res;
          });

        return res;
      }
    },
    {
      enabled:
        (!!sender_pid &&
          !!currency_pid &&
          (!debouncedSearchValue ||
            debouncedSearchValue.length > 2 ||
            !getBarcode)) ||
        !!currentPage ||
        !!isExisted
    }
  );

  useEffect(() => {
    if (data) {
      setPageCount(Math.ceil(data?.count / 10));
    }
  }, [data]);

  const loading = isLoading || isFetching;

  const newSearchData: SearchDataType[] = useMemo(() => {
    return (
      data?.products?.map((items: any) => {
        const barcode =
          data?.is_product === true
            ? items?.barcode
            : items?.nomenclature?.barcode;

        return {
          product_name: items?.nomenclature?.name,
          variation:
            items?.nomenclature?.is_variation === true ? t('yes') : t('no'),
          units: items?.nomenclature?.measure_unit?.name?.[i18n?.language],

          // this dataes need for static rendering dataes
          unit: items?.nomenclature?.measure_unit?.name?.[i18n?.language],
          measure_unit_pid: items?.nomenclature?.measure_unit?.pid,
          amount: items?.quantity_in ?? 0,
          quantity_asked: 0,
          total_quantity: items?.total_quantity ?? 0,
          current_balance: items?.quantity_in ?? 0,
          full_cost: items?.price_whosale ?? 0,
          nomenclature_pid: items?.nomenclature_pid,
          product_pid: items?.pid,
          package_quantity: items?.package_quantity,
          product_parent_pid: items?.pid,
          price: items?.price ?? 0,
          price_in: items?.price_in ?? 0,
          price_whosale: items?.price_whosale ?? 0,
          is_vat: items?.is_vat ? items?.is_vat : false,
          is_selected: items?.is_selected || false,
          measure_unit_kind_pid: items?.nomenclature?.measure_unit_kind_pid,
          currency: items?.currency?.name?.[i18n?.language],
          currency_pid: items?.currency_pid || 0,
          barcode: barcode,
          remainder: items?.quantity_in ?? 0,
          gupid: items?.pid || 0
        };
      }) ?? []
    );
  }, [data, searchValue, t, i18n?.language]);

  useEffect(() => {
    const selectedItems = newSearchData.filter(item => item.is_selected);
    selectedItems.forEach(item => {
      const isAlreadySelected = selectedData.some(
        (selected: SearchDataType) => selected.barcode === item.barcode
      );
      if (!isAlreadySelected) {
        setSelectedData((prev: SearchDataType[]) => [...prev, item]);
      }
    });
  }, [newSearchData]);

  const handleAddProduct = (product: SearchDataType) => {
    const isSelected = selectedData.some(
      (item: SearchDataType) => item.barcode === product.barcode
    );

    if (isSelected) return; // prevent re-adding

    if (isForceQuantity) {
      setAddedProduct(product);
      setOpenAddProduct(true);
    } else {
      setSelectedData((prev: any) => [...prev, product]);
      dispatch(
        showAlert({
          title: t('product_successfully_added'),
          type: 'success'
        })
      );
    }
  };

  const handleRemoveProduct = (product: SearchDataType) => {
    setSelectedData((prev: any) =>
      prev.filter((item: any) => item.barcode !== product.barcode)
    );
    dispatch(
      showAlert({
        title: t('product_removed_successfully'),
        type: 'success'
      })
    );
  };

  const searchTableColumns: ColumnDef<SearchDataType>[] = [
    {
      id: 'action',
      cell: rowData => {
        const product = rowData.row.original;
        const isSelected = selectedData.some(
          (item: SearchDataType) => item.barcode === product.barcode
        );

        return (
          <div className="d-flex align-items-center justify-content-center gap-2">
            {/* Add or Check Icon */}
            {isSelected ? (
              <FeatherIcon
                icon="check"
                className="text-success cursor-pointer"
                size={18}
              />
            ) : (
              <Button
                variant="hover"
                onClick={() => {
                  if (!isSelected) handleAddProduct(product);
                }}
              >
                <FeatherIcon icon="plus" className="cursor-pointer" size={18} />
              </Button>
            )}

            {/* Trash Icon only when selected */}
            {isSelected && (
              <Button
                variant="hover"
                onClick={() => handleRemoveProduct(product)}
              >
                <FeatherIcon
                  icon="trash-2"
                  className="cursor-pointer text-danger"
                  size={18}
                />
              </Button>
            )}
          </div>
        );
      },
      meta: {
        headerProps: { style: { width: '8%' } },
        cellProps: { className: 'text-900 p-2' }
      }
    },
    {
      accessorKey: 'product_name',
      header: t('product_names'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'barcode',
      header: t('barcode'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'units',
      header: t('measure_unit'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'price_whosale',
      header: t('price'),
      cell: rowData => {
        const { price_whosale } = rowData.row.original;

        return (
          <>
            {price_whosale
              ? new Intl.NumberFormat('en-US', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: true
                })
                  .format(price_whosale)
                  .replace(/,/g, ' ')
              : 0}
          </>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: newSearchData,
    columns: searchTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  const handleSave = () => {
    const createData = selectedData?.map((el: any) => {
      return {
        barcode: el?.barcode,
        name: el?.product_name,
        price_wholesale: el?.full_cost,
        quantity_asked: +el?.quantity_asked,
        currency_pid: el?.currency_pid || 0,
        measure_unit_pid: el?.measure_unit_pid || 0,
        state: 1,
        package_quantity: el?.package_quantity,
        nomenclature_pid: el?.nomenclature_pid || 0,
        product_pid: el?.product_pid || 0,
        product_parent_pid: el?.product_parent_pid || 0,
        price: el?.price,
        price_in: el?.price_in,
        price_whosale: el?.price_whosale
      };
    });

    const updateData = {
      order_pid: newOrderPid || 0,
      order_items: createData
    };

    const { amount_limit } = chartererData || {};
    const totalCost = createData.reduce(
      (sum: any, item: any) =>
        sum + (item.quantity_asked || 0) * (item.price_whosale || 0),
      0
    );

    if (amount_limit < totalCost) {
      dispatch(
        showAlert({
          title: t('you_have_amount_limit')
        })
      );
      setLoadingButton(false);

      return;
    }

    if (
      newOrderPid !== 0 &&
      (amount_limit === undefined ||
        amount_limit === 0 ||
        amount_limit >= totalCost)
    ) {
      setLoadingButton(true);
      orderService.getOrderById(newOrderPid).then((res: any) => {
        if (res) {
          orderOutService
            .updateOrderOut(newOrderPid, {
              ...res,
              state: 1
            })
            .then((outresponse: any) => {
              if (outresponse) {
                orderItemService
                  .createOrderItem(updateData)
                  .then((createres: any) => {
                    if (createres) {
                      dispatch(
                        showAlert({
                          title: t('new_application_saved'),
                          type: 'success'
                        })
                      );

                      const newData =
                        createres?.order_items?.map((items: any) => {
                          const packageQuantity = items?.package_quantity || 1;
                          const quantity = items?.quantity_asked
                            ? Number(items?.quantity_asked.toFixed(3))
                            : 0;

                          const fullPackages = Math.floor(
                            quantity / packageQuantity
                          );
                          const remainingQuantity = Math.floor(
                            quantity % packageQuantity
                          );
                          const quantityDisplay = `${fullPackages}/${remainingQuantity}`;

                          return {
                            product_name: items?.nomenclature?.name,
                            barcode: items?.nomenclature?.barcode,
                            unit: items?.nomenclature?.measure_unit?.name?.[
                              i18n?.language
                            ],
                            measure_unit_pid:
                              items?.nomenclature?.measure_unit_pid || 0,
                            amount:
                              items?.nomenclature?.measure_unit_kind_pid === 1
                                ? quantityDisplay
                                : items?.quantity_asked || 0,
                            full_cost: items?.price_wholesale ?? 0,
                            nomenclature_pid: items?.nomenclature_pid || 0,
                            package_quantity:
                              items?.package_quantity !== undefined
                                ? items.package_quantity
                                : 1,
                            product_pid: items?.product_pid || 0,
                            product_parent_pid: items?.product_pid || 0,
                            price: items?.price ?? 0,
                            price_in: items?.price_in ?? 0,
                            price_whosale: items?.price_whosale ?? 0,
                            is_vat: items?.is_vat ? items?.is_vat : false,
                            currency: items?.currency?.name?.[i18n?.language],
                            currency_pid: items?.currency_pid,
                            measure_unit_kind_pid:
                              items?.nomenclature?.measure_unit_kind_pid || 0,
                            gupid: items?.pid || 0
                          };
                        }) ?? [];

                      setOrderItemData([...newData]);
                      setSelectedData([]);
                      setLoadingButton(false);
                      setIshandleSave(false);
                      setOpenSearch(false);
                    }
                  });
              }
            });
        }
      });
    }
  };

  useEffect(() => {
    if (isHandleSave) {
      handleSave();
    }
  }, [isHandleSave]);

  return (
    <>
      <div className="d-flex gap-2 mb-2 align-items-center">
        <div className="form-icon-container">
          <Form.Floating>
            <Form.Control
              type="text"
              placeholder={t('enter_product')}
              onFocus={e => e.target.select()}
              onChange={e => setSearchValue(e.target.value)}
              className="form-icon-input"
            />
            <label
              htmlFor="floatingInputCustom"
              className="form-icon-label text-700"
            >
              {t('enter_product')}
            </label>
          </Form.Floating>
          <FontAwesomeIcon
            icon={faSearch}
            className="text-900 fs-9 form-icon"
          />
        </div>

        <div className="form-icon-container">
          <Form.Floating>
            <Form.Control
              type="text"
              placeholder={t('scan_barcode')}
              onFocus={e => e.target.select()}
              onChange={e => setGetBarcode(e.target.value)}
              className="form-icon-input"
            />
            <label
              htmlFor="floatingInputCustom"
              className="form-icon-label text-700"
            >
              {t('scan_barcode')}
            </label>
          </Form.Floating>
          <FontAwesomeIcon
            icon={faBarcode}
            className="text-900 fs-9 form-icon"
          />
        </div>
        <Form.Check
          checked={isExisted}
          onChange={e => {
            setIsExisted(e.target.checked);
          }}
          label={t('show_all_remainders')}
          className="cursor-pointer"
        />
        <div className="ms-auto">
          <Button
            className="d-flex align-items-center gap-2"
            variant="primary"
            loading={loadingButton}
            loadingPosition="start"
            onClick={() => setIshandleSave(true)}
          >
            <FontAwesomeIcon icon={faPlus} />
            {windowSize > 768 && t('add')}
          </Button>
        </div>
      </div>
      <AdvanceTableProvider {...table}>
        <div>
          <AdvanceTable
            tableProps={{ className: 'phoenix-table fs-9' }}
            isLoading={loading}
          />

          {loadingButton && (
            <div className="loading-table">
              <span>
                <Spinner
                  animation="border"
                  role="status"
                  style={{
                    color: '#8200BF',
                    width: '3rem',
                    height: '3rem'
                  }}
                ></Spinner>
              </span>
            </div>
          )}
        </div>
        <AdvanceTableCPagination
          count={pageCount}
          page={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </AdvanceTableProvider>

      <Modal
        show={openAddProduct}
        onHide={() => setOpenAddProduct(false)}
        size="sm"
        centered
        contentClassName="border border-300"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t('add')} {t('quantity')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AddIsForsedQuantity
            setOpenAddProduct={setOpenAddProduct}
            addedProduct={addedProduct}
            selectedData={selectedData}
            setSelectedData={setSelectedData}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

export default NewAppSearchTable;
